/* global Office, document, window, setInterval */

// Detects the current UI theme and applies it as `data-theme` on <html>.
//
// The right signal depends on the platform:
//
//   • Web (Outlook on the web): the user picks a theme inside OWA, which is
//     independent of the OS/browser. That choice surfaces via
//     Office.context.officeTheme.bodyBackgroundColor. `prefers-color-scheme`
//     here reflects the OS/browser, NOT the OWA setting, so trusting it makes
//     us render the wrong theme when (e.g.) the PC is dark but OWA is light.
//     → Prefer officeTheme; fall back to prefers-color-scheme.
//
//   • Desktop (Outlook classic / Mac): officeTheme is cached by Office.js at
//     add-in load time and never updates, whereas `prefers-color-scheme`
//     tracks the OS light/dark toggle live and the webview honours it.
//     → Prefer prefers-color-scheme; fall back to officeTheme.
//
// Office.js emits no reliable theme-changed event across hosts, so we re-check
// on focus, visibility change, and via a low-frequency poll.

type Theme = "light" | "dark";

let currentTheme: Theme | null = null;
let pollHandle: ReturnType<typeof setInterval> | null = null;

function hexLuminance(hex: string): number | null {
  const m = hex.match(/#?([0-9a-f]{6})/i);
  if (!m) return null;
  const r = parseInt(m[1].substring(0, 2), 16);
  const g = parseInt(m[1].substring(2, 4), 16);
  const b = parseInt(m[1].substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

// `prefers-color-scheme`, or null when the webview can't resolve it. Some
// webviews return matches=false for BOTH dark and light queries when the host
// doesn't expose the preference — treat that as "unknown".
function osPreference(): Theme | null {
  if (typeof window === "undefined" || !window.matchMedia) return null;
  const dark = window.matchMedia("(prefers-color-scheme: dark)");
  const light = window.matchMedia("(prefers-color-scheme: light)");
  if (dark.matches || light.matches) return dark.matches ? "dark" : "light";
  return null;
}

// Theme implied by the Office host's body background colour, or null when the
// host doesn't expose it. On web this tracks the OWA theme setting.
function officePreference(): Theme | null {
  try {
    const bg = Office.context.officeTheme?.bodyBackgroundColor;
    if (bg) {
      const lum = hexLuminance(bg);
      if (lum !== null) return lum < 0.5 ? "dark" : "light";
    }
  } catch {
    /* Outlook host without theme support */
  }
  return null;
}

function isWeb(): boolean {
  try {
    return Office.context.platform === Office.PlatformType.OfficeOnline;
  } catch {
    return false;
  }
}

function detect(): Theme {
  // On web, the OWA theme setting (officeTheme) wins over the OS/browser
  // preference; on desktop the live OS preference wins over the cached
  // officeTheme. Fall back to the other signal, then to light.
  const order: Array<() => Theme | null> = isWeb()
    ? [officePreference, osPreference]
    : [osPreference, officePreference];

  for (const get of order) {
    const t = get();
    if (t) return t;
  }
  return "light";
}

export function applyTheme(): Theme {
  const theme = detect();
  if (theme !== currentTheme) {
    currentTheme = theme;
    document.documentElement.setAttribute("data-theme", theme);
  }
  return theme;
}

export function initThemeWatcher(): void {
  applyTheme();

  // Live OS-level change.
  if (typeof window !== "undefined" && window.matchMedia) {
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyTheme();
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", handler);
    } else if (typeof (mql as unknown as { addListener?: (cb: () => void) => void }).addListener === "function") {
      (mql as unknown as { addListener: (cb: () => void) => void }).addListener(handler);
    }
  }

  // Re-check on focus / visibility for desktop hosts that batch updates.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") applyTheme();
  });
  window.addEventListener("focus", () => applyTheme());

  // Low-frequency poll as a final safety net.
  if (!pollHandle) {
    pollHandle = setInterval(() => {
      if (document.visibilityState === "visible") applyTheme();
    }, 3000);
  }
}
